import React, { useEffect, useState } from "react";
import { FormattedMessage, FormattedHTMLMessage, useIntl } from "react-intl";
import cn from "classnames";

import Seo from "../../../common/helpers/seo";

import routing from "../../routing";
import PlanCard from "../../components/PlanCard";
import Breadcrumb from "../../components/Breadcrumb";
import Alert from "../../components/Alert";
import Loader, { LoaderWrapper } from "../../components/Loader";
import { get, post } from "../../actions/general";

import ResultTable from "./ResultTable";

import messages from "./messages";

import { StyledLoader, StyledHomeworkResult, Instructions, CardContainer, PageWrapper, ResultsWrapper } from "./styles";
import { IPlan } from "../../../common/types";
import { useHistory, useRouteMatch } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { IResultsResponse } from "./interfaces";
import { AxiosResponse } from "axios";
import { IResult } from "./ResultTable/interfaces";
import { IHomework } from "../homework/HomeworkList/interfaces";
import { ICheckboxEvent } from "../../types";
import { IStateTree } from "../../store/interfaces";
import { IUser } from "../../store/user/models";
import Method from "../../components/Method";
import QrCodeToDownloadApp from "../../components/QrCodeToDownloadApp";
import DownloadCSV from "./DownloadCSV";

const removeSimilars = (results: IResult[]) => {
    const filteredResults: IResult[] = [];
    results.forEach(result => {
        const stringifiedResult = JSON.stringify(result.result);
        if (!filteredResults.find(filteredResult => filteredResult.name === result.name && stringifiedResult === JSON.stringify(filteredResult.result))) {
            filteredResults.push(result);
        }
    });
    return filteredResults;
};

const HomeworkResult = () => {
    const { formatMessage } = useIntl();
    const { key } = useSelector((state: IStateTree) => state.user as IUser)
    const dispatch = useDispatch();
    const match = useRouteMatch<{ id: string }>();
    const history = useHistory();
    const [plan, setPlan] = useState<IPlan | null>(null);
    const [results, setResults] = useState<IResult[]>([]);
    const [homework, setHomework] = useState<IHomework | null>(null);
    const [isExperience, setIsExperience] = useState(false);
    const getResults = () => {
        const { id } = match.params;
        dispatch(get(`homework-results/${id}`, (res: AxiosResponse<IResultsResponse>) => {
            const { plan, results, homework, isExperience } = res.data;
            setPlan(plan);
            setResults(removeSimilars(results));
            setHomework(homework);
            setIsExperience(isExperience);
        }));
    }
    useEffect(() => {
        getResults();
    }, []);

    const deleteHomework = () => {
        const { id } = match.params;
        dispatch(post(`delete-homework?id=${id}`, {}, () => {
            history.replace(`/${routing.dashboard}`);
        }));
    };

    const renameHomework = (_id: string, name: string, cb: (result: boolean) => void) => {
        dispatch(post(
            `rename-homework?id=${_id}`,
            { name },
            () => {
                setHomework({
                    ...homework!,
                    title: name,
                });
                cb(true);
            },
            () => cb(false)
        ));
    };

    const modifyHomeworkDate = (startDate: Date, date: Date, cb: () => void) => {
        const { id } = match.params;
        dispatch(post(`change-homework-date?id=${id}`, { date: date.getTime(), startDate: startDate.getTime() }, () => {
            setHomework({
                ...homework!,
                date: date.getTime(),
                startDate: startDate.getTime(),
            })
            cb();
        }));
    };

    const modifyHomeworkDescription = (description: string, cb: () => void) => {
        const { id } = match.params;
        dispatch(post(`change-homework-description?id=${id}`, { description }, () => {
            setHomework({
                ...homework!,
                description,
            });
            cb();
        }));
    };

    const handleArchivedChange = ({ target: { checked } }: ICheckboxEvent, { _id }: IHomework) => {
        dispatch(post(`archive-homework/${_id}`, { isArchived: checked }, () => {
            setHomework({
                ...homework!,
                isArchived: checked,
            })
        }));
    };

    return (
        <StyledHomeworkResult as="section" marginTop={50} className={cn("spring--shadow")}>
            {!homework ? (
                <StyledLoader>
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                </StyledLoader>
            ) : (
                <PageWrapper>
                    <div>
                        <Breadcrumb
                            className={homework.className}
                            classId={homework.classId}
                            plan={homework.title}
                        />
                    </div>
                    {!plan ? (
                        <Seo title={formatMessage(messages.homework)} />
                    ) : (
                        <Seo title={`${formatMessage(messages.homework)}: ${homework.title}`} />
                    )}
                    {plan && (
                        <CardContainer>
                            <PlanCard
                                plan={{ ...homework, name: homework.title, cover: plan.cover } as unknown as IPlan}
                                homework
                                homeworkResult
                                deleteHomework={deleteHomework}
                                renameHomework={renameHomework}
                                modifyHomeworkDate={modifyHomeworkDate}
                                modifyHomeworkDescription={modifyHomeworkDescription}
                                handleArchivedChange={e => handleArchivedChange(e, homework)}
                            />
                        </CardContainer>
                    )}
                    {plan && results.length > 0 && (
                        <ResultsWrapper>
                            <ResultTable plan={plan} results={results} isExperience={isExperience} />
                        </ResultsWrapper>
                    )}
                    {plan && results.length > 0 && (
                        <DownloadCSV homeworkId={match.params.id} title={homework.title} />
                    )}
                    {plan && (
                        <Instructions>
                            {results.length === 0 && (
                                <h4>
                                    <FormattedMessage {...messages.noSolutionYet} />
                                </h4>
                            )}
                            <p>
                                <FormattedMessage {...messages.step0} />
                            </p>
                            <ol>
                                <li className="ListStyleLowered">
                                    <FormattedHTMLMessage
                                        {...messages.appStep1a}
                                    />{" "}(<QrCodeToDownloadApp />){" "}
                                    <FormattedHTMLMessage
                                        {...messages.appStep1b}
                                    />
                                    
                                </li>
                            </ol>
                            <Method className="Method">
                                <span>
                                    <FormattedMessage {...messages.or} />
                                </span>
                            </Method>
                            <ol>
                                <li className="HiddenListStyle">
                                    <FormattedMessage
                                        {...messages.step1}
                                        values={{
                                            link: (
                                                <a href="/homework" target="_blank" className="link">
                                                    musicators.com/homework
                                                </a>
                                            ),
                                        }}
                                    />
                                </li>
                                <li>
                                    <FormattedMessage {...messages.step2} values={{ key: <b>{key}</b> }} />
                                </li>
                                <li>
                                    <FormattedMessage {...messages.step3} />
                                </li>
                            </ol>

                            <p>
                                <FormattedMessage {...messages.postNote} />
                            </p>

                            <Alert>
                                <FormattedHTMLMessage {...messages.noFacebook} />
                            </Alert>
                        </Instructions>
                    )}
                </PageWrapper>
            )}
        </StyledHomeworkResult>
    );
}

export default HomeworkResult;
